import { useState, useEffect } from 'react';
import { RiskAnalytics } from '../components/RiskAnalytics';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Plus, Search, AlertTriangle, Shield, ShieldAlert, ShieldCheck,
  MoreVertical, Edit, Trash2, TrendingUp, AlertCircle
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RISK_STATUSES = ['open', 'mitigated', 'closed'];
const RISK_LEVELS = ['low', 'medium', 'high', 'critical'];
const RISK_CATEGORIES = ['financial', 'operational', 'technical', 'legal', 'reputational', 'strategic', 'other'];

const statusColors = {
  open: 'bg-amber-100 text-amber-700',
  mitigated: 'bg-blue-100 text-blue-700',
  closed: 'bg-slate-100 text-slate-700'
};

const levelColors = {
  low: 'bg-green-100 text-green-700',
  medium: 'bg-yellow-100 text-yellow-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700'
};

export const RisksPage = () => {
  const [risks, setRisks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  
  // Dialog states
  const [showRiskDialog, setShowRiskDialog] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  
  // Form state
  const [riskForm, setRiskForm] = useState({
    title: '',
    description: '',
    project_id: '',
    category: 'operational',
    level: 'medium',
    status: 'open',
    impact: '',
    probability: '',
    mitigation_plan: '',
    owner: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [risksRes, projectsRes] = await Promise.all([
        axios.get(`${API}/risks`),
        axios.get(`${API}/projects`)
      ]);
      setRisks(risksRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load risks');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRisk = async () => {
    if (!riskForm.title) {
      toast.error('Risk title is required');
      return;
    }
    try {
      if (editingRisk) {
        await axios.put(`${API}/risks/${editingRisk.id}`, riskForm);
        toast.success('Risk updated');
      } else {
        await axios.post(`${API}/risks`, riskForm);
        toast.success('Risk created');
      }
      setShowRiskDialog(false);
      setEditingRisk(null);
      setRiskForm({ title: '', description: '', project_id: '', category: 'operational', level: 'medium', status: 'open', impact: '', probability: '', mitigation_plan: '', owner: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to save risk');
    }
  };

  const handleDeleteRisk = async (riskId) => {
    if (!confirm('Delete this risk?')) return;
    try {
      await axios.delete(`${API}/risks/${riskId}`);
      toast.success('Risk deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete risk');
    }
  };

  const openEditDialog = (risk) => {
    setEditingRisk(risk);
    setRiskForm({
      title: risk.title || '',
      description: risk.description || '',
      project_id: risk.project_id || '',
      category: risk.category || 'operational',
      level: risk.level || 'medium',
      status: risk.status || 'open',
      impact: risk.impact || '',
      probability: risk.probability || '',
      mitigation_plan: risk.mitigation_plan || '',
      owner: risk.owner || ''
    });
    setShowRiskDialog(true);
  };

  // Filter risks
  const filteredRisks = risks.filter(risk => {
    const matchesSearch = risk.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          risk.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || risk.status === statusFilter;
    const matchesLevel = levelFilter === 'all' || risk.level === levelFilter;
    return matchesSearch && matchesStatus && matchesLevel;
  });

  // Stats
  const totalRisks = risks.length;
  const openRisks = risks.filter(r => r.status === 'open').length;
  const criticalRisks = risks.filter(r => r.level === 'critical' && r.status === 'open').length;
  const mitigatedRisks = risks.filter(r => r.status === 'mitigated').length;

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || '-';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading risks...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="risks-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Risk Register</h1>
          <p className="text-muted-foreground">Identify, assess, and manage organizational risks</p>
        </div>
        <Button onClick={() => { setEditingRisk(null); setRiskForm({ title: '', description: '', project_id: '', category: 'operational', level: 'medium', status: 'open', impact: '', probability: '', mitigation_plan: '', owner: '' }); setShowRiskDialog(true); }}>
          <Plus className="h-4 w-4 mr-2" /> New Risk
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-black text-[#D4E100] flex items-center justify-center">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalRisks}</p>
              <p className="text-sm text-muted-foreground">Total Risks</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center">
              <ShieldAlert className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold">{openRisks}</p>
              <p className="text-sm text-muted-foreground">Open</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-red-100 text-red-600 flex items-center justify-center">
              <AlertCircle className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold">{criticalRisks}</p>
              <p className="text-sm text-muted-foreground">Critical</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-bold">{mitigatedRisks}</p>
              <p className="text-sm text-muted-foreground">Mitigated</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analytics: heat map, ageing, trend */}
      <RiskAnalytics />

      {/* Filters */}
      <div className="flex gap-4 items-center flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search risks..." 
            className="pl-10" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {RISK_STATUSES.map(s => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={levelFilter} onValueChange={setLevelFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Risk Level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Levels</SelectItem>
            {RISK_LEVELS.map(l => (
              <SelectItem key={l} value={l}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Risks Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Risk</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Level</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRisks.map(risk => (
                <TableRow key={risk.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{risk.title}</p>
                      {risk.description && <p className="text-xs text-muted-foreground line-clamp-1">{risk.description}</p>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm capitalize">{risk.category || '-'}</TableCell>
                  <TableCell className="text-sm">{getProjectName(risk.project_id)}</TableCell>
                  <TableCell>
                    <Badge className={levelColors[risk.level]}>{risk.level}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={statusColors[risk.status]}>{risk.status}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">{risk.owner || '-'}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(risk)}>
                          <Edit className="h-4 w-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleDeleteRisk(risk.id)} className="text-red-600">
                          <Trash2 className="h-4 w-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {filteredRisks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No risks found. Create one to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Risk Dialog */}
      <Dialog open={showRiskDialog} onOpenChange={setShowRiskDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingRisk ? 'Edit Risk' : 'Create New Risk'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Risk Title *</Label>
              <Input 
                value={riskForm.title}
                onChange={(e) => setRiskForm({ ...riskForm, title: e.target.value })}
                placeholder="Enter risk title"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={riskForm.description}
                onChange={(e) => setRiskForm({ ...riskForm, description: e.target.value })}
                placeholder="Describe the risk"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Project</Label>
                <Select value={riskForm.project_id} onValueChange={(v) => setRiskForm({ ...riskForm, project_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Link to project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Project</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={riskForm.category} onValueChange={(v) => setRiskForm({ ...riskForm, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RISK_CATEGORIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Owner</Label>
                <Input 
                  value={riskForm.owner}
                  onChange={(e) => setRiskForm({ ...riskForm, owner: e.target.value })}
                  placeholder="Risk owner"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Risk Level</Label>
                <Select value={riskForm.level} onValueChange={(v) => setRiskForm({ ...riskForm, level: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RISK_LEVELS.map(l => (
                      <SelectItem key={l} value={l}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={riskForm.status} onValueChange={(v) => setRiskForm({ ...riskForm, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RISK_STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Impact</Label>
                <Input 
                  value={riskForm.impact}
                  onChange={(e) => setRiskForm({ ...riskForm, impact: e.target.value })}
                  placeholder="Potential impact"
                />
              </div>
              <div className="space-y-2">
                <Label>Probability</Label>
                <Input 
                  value={riskForm.probability}
                  onChange={(e) => setRiskForm({ ...riskForm, probability: e.target.value })}
                  placeholder="Likelihood of occurrence"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Mitigation Plan</Label>
              <Textarea 
                value={riskForm.mitigation_plan}
                onChange={(e) => setRiskForm({ ...riskForm, mitigation_plan: e.target.value })}
                placeholder="How will this risk be mitigated?"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRiskDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveRisk}>{editingRisk ? 'Update Risk' : 'Create Risk'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RisksPage;
